// frontend/src/ui/sceneLoading.js
import { GAME_WIDTH, GAME_HEIGHT, assetUrl } from '../config.js';
import { ASSETS } from '../assetManifest.mjs';
import { gameState } from '../state/gameState.js';
import { currentPhase } from '../state/dayCycle.js';
import { preloader } from '../scenes/PreloadScene.js';
import { iconText } from './iconText.js';
import { ANIM_ENABLED } from './walkAnim.js';

// Scene-side half of staged loading: a scene asks for what it draws (needsFor) and loads
// only what the caches lack — behind the short bar — while PreloadScene keeps streaming the
// rest. `queueMissing` is for `preload()` (Phaser waits for the loader itself);
// `ensureLoaded` for `create()`-time needs (a visit's host state arrives there).

export function loadCtx(over = {}) {
  return {
    phase: currentPhase(),
    character: (typeof window !== 'undefined' ? window.__myCharacter : null) ?? 'natalka',
    look: (typeof window !== 'undefined' ? window.__myLook : null) ?? null,   // the car picture of her hairstyle
    anim: ANIM_ENABLED,          // flat-art mode (?anim=0) needs the other character's clothes pictures as body art
    equipped: gameState.raw?.inventory?.equipped ?? null,
    state: gameState.raw ?? {},
    ...over,
  };
}

export function loadingBar(scene, text = 'Ładowanie…') {
  const cam = scene.cameras.main;
  const x0 = cam.scrollX, y0 = cam.scrollY;
  const bg = scene.add.rectangle(x0, y0, GAME_WIDTH, GAME_HEIGHT, 0x111418).setOrigin(0, 0).setDepth(9000);
  const label = iconText(scene, x0 + GAME_WIDTH / 2, y0 + GAME_HEIGHT / 2 - 30, text,
    { fontFamily: 'system-ui', fontSize: '24px', color: '#ffffff' }).setOrigin(0.5).setDepth(9001);
  const bar = scene.add.rectangle(x0 + GAME_WIDTH / 2 - 200, y0 + GAME_HEIGHT / 2 + 10, 0, 18, 0xffd166).setOrigin(0, 0.5).setDepth(9001);
  const onProgress = (p) => { bar.width = 400 * p; };
  scene.load.on('progress', onProgress);
  return {
    destroy() { scene.load.off('progress', onProgress); bg.destroy(); label.destroy(); bar.destroy(); },
  };
}

// `ids` are manifest ids (texture keys, or `audio:<key>` for sounds).
function missing(scene, ids) {
  return [...new Set(ids)].filter((id) => {
    const a = ASSETS[id];
    if (!a) return false;
    return !(a.kind === 'audio' ? scene.cache.audio.exists(a.key) : scene.textures.exists(a.key));
  });
}

function queue(scene, ids) {
  // Tell the background loader these are ours until our `complete` fires, so it doesn't
  // start the same files in parallel ("Texture key already in use"). Released below.
  preloader.claim(ids);
  for (const id of ids) {
    const a = ASSETS[id];
    if (a.kind === 'audio') scene.load.audio(a.key, assetUrl(`/assets/${a.path}`));
    else scene.load.image(a.key, assetUrl(`/assets/${a.path}`));
  }
  scene.load.once('loaderror', (file) => console.warn(`[${scene.scene.key}] missing ${file.key} (fallback)`));
}

// Whatever the scene's own loader could not fetch (a server restart, a flaky link) goes to
// the front of the background loader; renderBackground/NatalkaSprite/whenReady swaps draw
// it in when it lands, so the scene heals instead of showing a checkerboard until re-entry.
function retryFailed(scene, ids) {
  const left = missing(scene, ids);
  if (!left.length) return;
  console.warn(`[${scene.scene.key}] ${left.length} file(s) failed to load, retrying in the background:`, left.join(', '));
  preloader.retry(left);
}

/** Inside preload(): queue what this scene lacks; Phaser runs the loader before create(). */
export function queueMissing(scene, keys) {
  const need = missing(scene, keys);
  if (!need.length) return 0;
  const bar = loadingBar(scene);
  // The background loader may have waiters on these keys (whenReady): tell it they landed.
  scene.load.once('complete', () => { bar.destroy(); preloader.sweep(); preloader.release(need); retryFailed(scene, need); });
  queue(scene, need);
  return need.length;
}

/** Inside create() or later: load what is missing now and resolve when it is in. */
export function ensureLoaded(scene, keys) {
  const need = missing(scene, keys);
  if (!need.length) return Promise.resolve();
  return new Promise((resolve) => {
    const bar = loadingBar(scene);
    scene.load.once('complete', () => { bar.destroy(); preloader.sweep(); preloader.release(need); retryFailed(scene, need); resolve(); });
    queue(scene, need);
    scene.load.start();
  });
}

/** Tell the background loader where we are, so its next tiers are the right ones. */
export function prioritiseFor(sceneKey, over = {}) {
  preloader.prioritise(sceneKey, loadCtx(over));
}
